import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  consumeMagicLink,
  fetchAuthSession,
  fetchOidcConfig,
  requestMagicLink,
  signOut,
  type ApiErrorResponse,
  type AuthMeResponse,
  type MagicLinkConsumePayload,
  type MagicLinkConsumeResponse,
  type MagicLinkRequestPayload,
  type MagicLinkRequestResponse,
  type LogoutResponse,
  type OidcConfig,
} from '../services/auth';

export const authKeys = {
  root: ['auth'] as const,
  session: () => [...authKeys.root, 'session'] as const,
  oidcConfig: () => [...authKeys.root, 'oidc-config'] as const,
};

export const useAuthSession = () => {
  return useQuery<AuthMeResponse | null, ApiErrorResponse>(authKeys.session(), fetchAuthSession, {
    retry: false,
  });
};

export const useRequestMagicLink = () => {
  return useMutation<MagicLinkRequestResponse, ApiErrorResponse, MagicLinkRequestPayload>((payload) =>
    requestMagicLink(payload),
  );
};

export const useConsumeMagicLink = () => {
  const client = useQueryClient();
  return useMutation<MagicLinkConsumeResponse, ApiErrorResponse, MagicLinkConsumePayload>(
    (payload) => consumeMagicLink(payload),
    {
      onSuccess: (data) => {
        client.setQueryData(authKeys.session(), data.user ?? null);
      },
    },
  );
};

export const useSignOut = () => {
  const client = useQueryClient();
  return useMutation<LogoutResponse, ApiErrorResponse, void>(
    async () => {
      return signOut();
    },
    {
      onSettled: () => {
        client.setQueryData(authKeys.session(), null);
      },
    },
  );
};

export const useOidcConfig = () => {
  return useQuery<OidcConfig, ApiErrorResponse>(authKeys.oidcConfig(), fetchOidcConfig, {
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
    retry: false,
  });
};
